import {
  Users, ListChecks, CalendarDays, FileText, Zap, Shield, LayoutGrid,
  Server, BrainCircuit, Package, Bot, Workflow, Network, Handshake,
  KeyRound, Activity, Languages, Cable, Sparkles, ToolCase, PanelsTopLeft,
  Building2, Mail, FolderOpen, MessageSquareText, Video, Brain,
  FileType, Table, Presentation, ChartColumnIncreasing, Scale, Route,
  AudioLines, Clapperboard, LockKeyhole,
  type LucideIcon,
} from "lucide-react";

/**
 * Einheitliches Glyph-Set für ALLE Janeway-Module (Material-System statt
 * hand-gesetzter Raster). lucide = perfekt normalisierte Strichstärke, 24er-Grid
 * und optische Mitte → die Kacheln lesen als EINE Familie. Gekeyt auf die Modul-id.
 * Genutzt von `AppIcon` (Switcher/HubAppGrid) — die echten Raster-Logos bleiben für
 * PWA/Manifest/Hub-Identität (bis Umstellung auf das Glyph-System final ausgerollt ist).
 */
export const APP_GLYPHS: Record<string, LucideIcon> = {
  // L1 — Infrastruktur
  "janeway-infra": Server,
  // L2 — Core / SDKs
  "janeway-ai-core": BrainCircuit,
  "janeway-app-sdk": Package,
  "janeway-service-sdk": Cable,
  // L3 — Engines / Gateway
  "janeway-agent-engine": Bot,
  "janeway-workflow-engine": Workflow,
  "janeway-api": Network,
  // L4 — Anwendungen
  "janeway-admin": Shield,
  "janeway-affiliate": Handshake,
  "janeway-ai-chat": Sparkles,
  "janeway-auth": KeyRound,
  "janeway-automations": Zap,
  "janeway-calendar": CalendarDays,
  "janeway-contacts": Users,
  "janeway-crm": Route,
  // Beide standen hier NICHT und trugen deshalb `FALLBACK_GLYPH` — also
  // `LayoutGrid`, das Zeichen von janeway-hub. Ihr `app.manifest.ts` nannte
  // das richtige Glyph die ganze Zeit; nur kam es nie an. Seit 2026-09-10
  // haelt `manifest-glyphen-stehen-hier.test.ts` den Satz gegen die
  // Manifeste, damit die naechste App nicht dieselbe Runde dreht.
  "janeway-ai-studio": Clapperboard,
  "janeway-dictum": AudioLines,
  // LockKeyhole, NICHT KeyRound oder Shield: die gehoeren janeway-auth und
  // janeway-admin. Zwei Apps mit demselben Zeichen war bei cashflow der Fall.
  "janeway-pass": LockKeyhole,
  "janeway-hub": LayoutGrid,
  "janeway-notes": FileText,
  "janeway-office-tools": ToolCase,
  "janeway-status": Activity,
  "janeway-tasks": ListChecks,
  "janeway-translate": Languages,
  "janeway-api-docs": Network,
  // Steht bewusst NICHT in der statischen App-Liste weiter unten und in keiner
  // `infra_apps`-Zeile — der Legal-Hub gehoert weder in den Switcher noch in den
  // Hub. Genau deshalb MUSS sein Glyph hier stehen: die Auflösung ueber die
  // `glyph`-Spalte der Registry greift fuer ihn nie, und `AppIcon` faellt sonst
  // auf das generische Raster zurueck.
  "janeway-legal": Scale,
  "janeway-immo": Building2,
  "janeway-cashflow": ChartColumnIncreasing,
  "janeway-hosting": Server,
  "janeway-mail-client": Mail,
  "janeway-drive": FolderOpen,
  // Brain, nicht BrainCircuit: das gehoert ai-core. Zwei aehnliche Glyphen in
  // einer Kachelreihe lesen sich sonst als dieselbe App.
  "janeway-brain": Brain,
  // Drive-Host-Gesichter (dieselbe Codebase, per Subdomain): eigenes Glyph je
  // Typ-App, damit Header-Wortmarke + Icon nicht das Drive-Glyph zeigen. Muss
  // zu APP_MODE[mode].glyph in janeway-drive/lib/drive/app-mode.ts passen.
  // Bewusst NUR hier, nicht in JANEWAY_MODULES: es sind Typ-Gesichter derselben
  // Drive-Codebase (Host-Modes), keine eigenstaendigen Module/Deploys.
  "janeway-docs": FileType,
  "janeway-sheets": Table,
  "janeway-slides": Presentation,
  "janeway-messenger": MessageSquareText,
  "janeway-meet": Video,
  // L5 — UI-Bibliothek
  "janeway-gui-elements": PanelsTopLeft,
};

export const FALLBACK_GLYPH: LucideIcon = LayoutGrid;

/** Modul-Registry (Layer + Anzeigename + lucide-Glyph-Name) — Quelle für Review/Doku. */
export interface JanewayModule {
  id: string;
  name: string;
  layer: "L1" | "L2" | "L3" | "L4" | "L5";
  glyphName: string;
}
export const JANEWAY_MODULES: JanewayModule[] = [
  { id: "janeway-infra", name: "Infra", layer: "L1", glyphName: "Server" },
  { id: "janeway-ai-core", name: "AI Core", layer: "L2", glyphName: "BrainCircuit" },
  { id: "janeway-app-sdk", name: "App SDK", layer: "L2", glyphName: "Package" },
  { id: "janeway-service-sdk", name: "Service SDK", layer: "L2", glyphName: "Cable" },
  { id: "janeway-agent-engine", name: "Agent Engine", layer: "L3", glyphName: "Bot" },
  { id: "janeway-workflow-engine", name: "Workflow Engine", layer: "L3", glyphName: "Workflow" },
  { id: "janeway-api", name: "API Gateway", layer: "L3", glyphName: "Network" },
  { id: "janeway-admin", name: "Admin", layer: "L4", glyphName: "Shield" },
  { id: "janeway-affiliate", name: "Affiliate", layer: "L4", glyphName: "Handshake" },
  { id: "janeway-ai-chat", name: "AI Chat", layer: "L4", glyphName: "Sparkles" },
  { id: "janeway-auth", name: "Auth", layer: "L4", glyphName: "KeyRound" },
  { id: "janeway-automations", name: "Automations", layer: "L4", glyphName: "Zap" },
  { id: "janeway-calendar", name: "Calendar", layer: "L4", glyphName: "CalendarDays" },
  { id: "janeway-contacts", name: "Contacts", layer: "L4", glyphName: "Users" },
  { id: "janeway-crm", name: "CRM", layer: "L4", glyphName: "Route" },
  { id: "janeway-pass", name: "Pass", layer: "L4", glyphName: "LockKeyhole" },
  { id: "janeway-hub", name: "Hub", layer: "L4", glyphName: "LayoutGrid" },
  { id: "janeway-notes", name: "Notes", layer: "L4", glyphName: "FileText" },
  { id: "janeway-office-tools", name: "Office Tools", layer: "L4", glyphName: "ToolCase" },
  { id: "janeway-status", name: "Status", layer: "L4", glyphName: "Activity" },
  { id: "janeway-tasks", name: "Tasks", layer: "L4", glyphName: "ListChecks" },
  { id: "janeway-translate", name: "Translate", layer: "L4", glyphName: "Languages" },
  { id: "janeway-api-docs", name: "API Docs", layer: "L4", glyphName: "Network" },
  { id: "janeway-immo", name: "Immobilien", layer: "L4", glyphName: "Building2" },
  { id: "janeway-cashflow", name: "Cashflow", layer: "L4", glyphName: "ChartColumnIncreasing" },
  { id: "janeway-hosting", name: "Hosting", layer: "L4", glyphName: "Server" },
  { id: "janeway-mail-client", name: "Mail", layer: "L4", glyphName: "Mail" },
  { id: "janeway-drive", name: "Drive", layer: "L4", glyphName: "FolderOpen" },
  { id: "janeway-brain", name: "Brain", layer: "L4", glyphName: "Brain" },
  { id: "janeway-messenger", name: "Messenger", layer: "L4", glyphName: "MessageSquareText" },
  { id: "janeway-meet", name: "Meet", layer: "L4", glyphName: "Video" },
  { id: "janeway-gui-elements", name: "GUI Elements", layer: "L5", glyphName: "PanelsTopLeft" },
];
